<script lang="ts" setup>
type LinkType = {
  label: string;
  to?: string;
  items?: Omit<LinkType, 'items'>[];
};

const MODAL_KEY = 'main-menu';

const { t } = useI18n();
const modalStore = useModalStore();
const productCartStore = useProductCartStore();
const userStore = useUserStore();
const router = useRouter();
const route = useRoute();

const { data: commonData } = await useAsyncCommonData();

const isBackNavigationButton = computed(() => /^\/(auth|order)/.test(route.path));
const isBackNavigationButtonDark = computed(() => /^\/order/.test(route.path));

const links = computed<LinkType[][]>(() => [
  [
    {
      label: t('link.catalog'),
      to: '/catalog',
    },
    ...commonData.value.sections
      .filter((item) => !item.parentId)
      .map((section) => ({
        label: section.name,
        to: calculateSectionLink(section),
        items: commonData.value.sections
          .filter((item) => item.parentId === section.id)
          .map((item) => ({
            label: item.name,
            to: calculateSectionLink(item),
          })),
      })),
  ],
  [
    {
      label: t('link.main'),
      to: '/',
    },
    {
      label: t('link.about'),
      items: [
        {
          label: t('link.mission'),
          to: '/about',
        },
        {
          label: t('link.blog'),
          to: '/blog',
        },
        {
          label: t('link.social_responsibility'),
          to: '/social-responsibility',
        },
        {
          label: t('link.contacts'),
          to: '/contacts',
        },
      ],
    },
    {
      label: t('link.delivery_and_payment'),
      to: '/delivery-and-payment',
    },
    {
      label: t('link.faq'),
      to: '/faq',
    },
    {
      label: t('link.subscribe_product'),
      to: '/subscribe-product',
    },
  ],
]);

const onGlobalClick = () => {
  if (modalStore.modalKeys[MODAL_KEY]) {
    modalStore.closeModal(MODAL_KEY);
  }
};

const onOpenProductCart = () => {
  modalStore.closeModal(MODAL_KEY);
  productCartStore.open();
};

const onClickMenuButton = () => {
  const back = router.options.history.state.back;

  const navigateBack = () => {
    if (!back || route.path === back) router.push('/');
    else router.back();
  };

  if (modalStore.modalKeys[MODAL_KEY]) modalStore.closeModal(MODAL_KEY);
  else if (/^\/auth/.test(route.path)) {
    if (back && back !== '/profile') navigateBack();
    else router.push('/');
  } else if (/^\/order\/successful/.test(route.path)) router.push('/catalog');
  else if (/^\/order/.test(route.path)) navigateBack();
  else modalStore.openModal(MODAL_KEY);
};

watch(
  () => route.fullPath,
  () => {
    modalStore.closeModal(MODAL_KEY);
  },
);

onMounted(() => {
  window.addEventListener('click', onGlobalClick);
});

onBeforeUnmount(() => {
  modalStore.closeModal(MODAL_KEY);
  window.removeEventListener('click', onGlobalClick);
});
</script>

<template>
  <header class="mainMenu__header">
    <BaseUserButton
      v-if="!isBackNavigationButton"
      @click.stop="userStore.toProfile"
      :dark="isBackNavigationButtonDark || !!modalStore.modalKeys[MODAL_KEY]"
      :count="productCartStore.totalCount"
      :aria-label="t('action.open_user')"
      data-test-id="user-toggle"
    />
    <Transition name="fade">
      <BaseCartButton
        v-if="!isBackNavigationButton"
        @click.stop="onOpenProductCart"
        :dark="isBackNavigationButtonDark || !!modalStore.modalKeys[MODAL_KEY]"
        :count="productCartStore.totalCount"
        :aria-label="t('action.open_cart')"
        data-test-id="product-cart-toggle"
      />
    </Transition>
    <BaseModalButton
      @click.stop="onClickMenuButton"
      :burger="!modalStore.modalKeys[MODAL_KEY] && !isBackNavigationButton"
      :dark="isBackNavigationButtonDark"
      :aria-label="t('action.open_menu')"
      data-test-id="navigation-menu-toggle"
    />
  </header>

  <BaseSidebar
    @update:visible="modalStore.toggleModal(MODAL_KEY, $event)"
    :visible="!!modalStore.modalKeys[MODAL_KEY]"
    full-height
    tag="nav"
    class="mainMenu"
    data-test-id="navigation-menu"
  >
    <div class="mainMenu__content">
      <div v-for="(group, index) in links" :key="index" class="mainMenu__block">
        <template v-for="item in group" :key="item.label">
          <NuxtLink :to="item.to" class="mainMenu__item" :class="{ mainMenu__item_label: item.items?.length }">
            {{ item.label }}
          </NuxtLink>
          <div
            v-if="item.items?.length"
            class="mainMenu__group"
            :style="{ '--item-count': Math.max(group.length, item.items.length) + 1 }"
          >
            <NuxtLink v-for="subitem in item.items" :key="subitem.label" :to="subitem.to" class="mainMenu__item">
              {{ subitem.label }}
            </NuxtLink>
          </div>
        </template>
      </div>
    </div>
  </BaseSidebar>
</template>

<i18n>
ru:
  action:
    open_user: Открыть личный кабинет
    open_cart: Открыть корзину
    open_menu: Открыть навигационное меню
  link:
    main: Главная
    about: О бренде
    mission: Миссия
    contacts: Где найти
    faq: FAQ
    delivery_and_payment: Доставка и оплата
    subscribe_product: Подписка на рефилы
    blog: Статьи и гайды
    social_responsibility: Социальная ответственность
    catalog: Все продукты
  auth: Войти
  profile: Личный кабинет

en:
  action:
    open_user: Open profile
    open_cart: Open cart
    open_menu: Open navigation menu
  link:
    main: Main
    about: About
    mission: Mission
    contacts: Contacts
    faq: FAQ
    delivery_and_payment: Delivery and payment
    subscribe_product: Subscription for Refills
    blog: Articles and guides
    social_responsibility: Social responsibility
    catalog: All products
  auth: Authorization
  profile: Account
</i18n>

<style lang="scss">
.mainMenu {
  $self: &;

  --row-gap: 12px;

  width: 720px;
  max-width: 100%;
  background: #121212;

  @include mq('sm') {
    width: 100%;
  }

  &__header {
    position: fixed;
    display: flex;
    gap: 16px;
    top: 28px;
    right: 20px;
    pointer-events: none;
    z-index: 11;

    @include mq('sm') {
      gap: 8px;
      top: 10px;
      right: 5px;
    }

    & > * {
      pointer-events: auto;
    }
  }

  &__content {
    padding: 115px 45px 48px;
    display: flex;
    flex-direction: column;
    gap: 80px;

    @include mq('sm') {
      padding: 32px 16px;
    }
  }

  &__block {
    display: grid;
    grid-template-columns: 280px 1fr;
    row-gap: var(--row-gap);

    @include mq('sm') {
      grid-template-columns: 1fr;
    }

    & > #{$self}__item {
      grid-column: 1;
    }
  }

  &__item {
    font-size: 24px;
    line-height: 32px;
    letter-spacing: 0.2px;
    color: #ffffff;
    transition: opacity 0.3s ease;

    &:hover {
      opacity: 0.7;
    }

    @include mq('sm') {
      font-size: 30px;
      line-height: 38px;
    }

    &_label:hover {
      margin: calc(var(--row-gap) * -1) 0;
      padding: var(--row-gap) 0;

      & + #{$self}__group {
        opacity: 1;
        pointer-events: auto;
      }
    }
  }

  &__group {
    flex-direction: column;
    row-gap: var(--row-gap);
    grid-column: 2;
    grid-row: 1 / var(--item-count);
    display: flex;
    padding-left: 16px;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.3s ease;

    @include mq('sm') {
      grid-column: 1;
      grid-row: auto;
      opacity: 1;
      padding-left: 96px;
      padding-top: 8px;
      padding-bottom: 8px;
      pointer-events: auto;
    }

    &:hover {
      opacity: 1;
      pointer-events: auto;
    }
  }
}
</style>
